import React, { useEffect, useRef, useState } from "react";
import AdyenCheckout from "@adyen/adyen-web";
import "@adyen/adyen-web/dist/adyen.css";
import { OnPaymentCompletedData, ResultCode } from "@adyen/adyen-web/dist/types/components/types";
import { ReactComponent as SpinnerIcon } from "../../../Assets/ActionIcons/SpinnerIcon.svg";
import { ReactComponent as CheckMarkIcon } from "../../../Assets/ActionIcons/CheckMarkIcon.svg";
import { ReactComponent as ExclamationWarningIcon } from "../../../Assets/ActionIcons/ExclamationWarningIcon.svg";
import {
  AdyenPaymentMethodType,
  getPaymentMethodDetails,
  GiactValidationResult,
  GiactValidationView
} from "../../../Contracts/Transaction";
import { AdyenEnvironment } from "../../../Contracts/AdyenSession";
import Modal from "../../Atoms/Modal";
import { useBannerStore } from "../../../Store/Banner";
import { BannerType } from "../../../Contracts/Banner";

interface PaymentFormProps {
  allowedPaymentMethods: AdyenPaymentMethodType[];
  fetchAdyenSessionForMethod: (method: AdyenPaymentMethodType) => Promise<void>;
  sessionData?: {
    session_id: string;
    session_data: string;
  };
  doGiactValidation: (billingAddress: any, method: any) => Promise<GiactValidationView>;
  beforeSubmitValidation?: (method: AdyenPaymentMethodType) => Promise<boolean>;
  callTransactionRoute: (
    method: AdyenPaymentMethodType
  ) => Promise<{ reference?: string; error?: string }>;
  callBacsPaymentRoute: (data: any, transactionReference: string) => Promise<boolean>;
  callNotifyRoute?: (result: ResultCode) => Promise<void>;
  onPaymentCompleted: (method: AdyenPaymentMethodType) => void;
  onError?: (error: Error) => void;
  translations?: Record<string, any>;
  onChangeDropInLoading?: (loading: boolean) => void;
  disableLoadingFirstMethodByDefault?: boolean;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  allowedPaymentMethods,
  fetchAdyenSessionForMethod,
  sessionData,
  doGiactValidation,
  onPaymentCompleted,
  beforeSubmitValidation,
  callTransactionRoute,
  callNotifyRoute,
  callBacsPaymentRoute,
  onError,
  translations = {},
  onChangeDropInLoading,
  disableLoadingFirstMethodByDefault = false
}) => {
  const paymentContainerRef = useRef<HTMLDivElement>(null);
  const [selectedMethod, setSelectedMethod] = useState<AdyenPaymentMethodType | null>(null);
  const [dropInLoading, setDropInLoading] = useState<boolean>(false);
  const [dropInMounted, setDropInMounted] = useState<boolean>(false);
  const [checkoutInstance, setCheckoutInstance] = useState<any>(null);
  const [giactValidationFailureMessageDialogue, setGiactValidationFailureMessageDialogue] =
    React.useState<{ open: boolean; error?: string }>({
      open: false
    });
  const { addBanner } = useBannerStore();

  React.useEffect(() => {
    if (onChangeDropInLoading) {
      onChangeDropInLoading(dropInLoading);
    }
  }, [dropInLoading, onChangeDropInLoading]);

  const handlePaymentMethodClick = async (method: AdyenPaymentMethodType) => {
    if (checkoutInstance) {
      checkoutInstance.unmount();
      setCheckoutInstance(null);
      setDropInMounted(false);
    }

    setSelectedMethod(method);
    setDropInLoading(true);

    try {
      await fetchAdyenSessionForMethod(method);
    } catch (err) {
      console.error("Error fetching Adyen session:", err);
      setDropInLoading(false);
      onError?.(err as Error);
    }
  };

  React.useEffect(() => {
    const createAdyenCheckout = async (
      method: AdyenPaymentMethodType,
      sessionData: {
        session_id: string;
        session_data: string;
      }
    ) => {
      const clientKey = process.env.REACT_APP_ADYEN_CHECKOUT_CLIENT_KEY || "";
      const adyenEnvironment: AdyenEnvironment =
        (process.env.REACT_APP_ADYEN_ENVIRONMENT as AdyenEnvironment) || "test";
      const selectedMethodWithAttributes = getPaymentMethodDetails(method);
      try {
        const checkout = await AdyenCheckout({
          environment: adyenEnvironment,
          clientKey,
          session: {
            id: sessionData.session_id,
            sessionData: sessionData.session_data
          },
          beforeSubmit: async (data, _component, actions) => {
            console.debug("Entered beforeSubmit");
            if (beforeSubmitValidation) {
              const valid = await beforeSubmitValidation(method);
              if (!valid) {
                console.warn("Validation failed");
                setDropInMounted(false);
                setDropInLoading(false);
                return actions.reject();
              }
            }
            if (method === AdyenPaymentMethodType.Bacs) {
              return actions.resolve();
            }
            if (method === AdyenPaymentMethodType.Ach) {
              const result = await doGiactValidation(data.billingAddress, data.paymentMethod);
              if (result.bank_verification_result !== GiactValidationResult.Passed) {
                console.warn("Giact validation failed");
                setGiactValidationFailureMessageDialogue({
                  open: true,
                  error: result.error
                });
                setDropInMounted(false);
                setDropInLoading(false);
                return actions.reject();
              }
            }

            let transactionResponse: { reference?: string; error?: string } | undefined;
            try {
              transactionResponse = await callTransactionRoute(method);
            } catch (error) {
              console.error("Error calling transaction route:", error);
              addBanner({
                type: BannerType.Error,
                message: "Failed to create transaction."
              });
              setDropInMounted(false);
              setDropInLoading(false);
              return actions.reject();
            }

            if (transactionResponse?.error) {
              addBanner({
                type: BannerType.Error,
                message: transactionResponse.error,
                closeable: true
              });
              setDropInMounted(false);
              setDropInLoading(false);
              return actions.reject();
            }

            console.debug("Created transaction!");

            return actions.resolve(data);
          },
          ...(method === AdyenPaymentMethodType.Bacs
            ? {
                onSubmit: async (data, _component) => {
                  console.debug("Entered onSubmit");
                  let transactionResponse: { reference?: string; error?: string } | undefined;
                  try {
                    transactionResponse = await callTransactionRoute(method);
                  } catch (error) {
                    console.error("Error calling transaction route:", error);
                    addBanner({
                      type: BannerType.Error,
                      message: "Failed to create transaction."
                    });
                    setDropInMounted(false);
                    setDropInLoading(false);
                    return;
                  }
                  if (!transactionResponse.reference) {
                    if (
                      transactionResponse.error &&
                      transactionResponse.error === "Adyen session expired"
                    ) {
                      addBanner({
                        type: BannerType.Error,
                        message:
                          "We're sorry, the payment session has expired. Please refresh the page and try again.",
                        closeable: true
                      });
                      setDropInMounted(false);
                      setDropInLoading(false);
                      return;
                    }
                    addBanner({
                      type: BannerType.Error,
                      message:
                        "We're sorry, an unexpected error occurred. Please refresh the page and try again.",
                      closeable: true
                    });
                    setDropInMounted(false);
                    setDropInLoading(false);
                    return;
                  }
                  const bacsResponse = await callBacsPaymentRoute(
                    {
                      bank_account_number: data?.data?.paymentMethod?.bankAccountNumber,
                      bank_location_id: data?.data?.paymentMethod?.bankLocationId,
                      checkout_attempt_id: data?.data?.paymentMethod?.checkoutAttemptId,
                      holder_name: data?.data?.paymentMethod?.holderName,
                      type: data?.data?.paymentMethod?.type
                    },
                    transactionResponse.reference
                  );
                  if (bacsResponse && callNotifyRoute) {
                    await callNotifyRoute("Received");
                  }
                  onPaymentCompleted(method);
                }
              }
            : {}),
          onPaymentCompleted: (response: OnPaymentCompletedData) => {
            console.debug("Entered onPaymentCompleted");
            if (callNotifyRoute) {
              callNotifyRoute(response.resultCode);
            }
            if (selectedMethod) {
              onPaymentCompleted(selectedMethod);
            }
          },
          onError: error => {
            console.error("Adyen Drop-In error:", error);
            onError?.(error);
            setDropInMounted(false);
            setDropInLoading(false);
          },
          analytics: {
            enabled: true
          },
          paymentMethodsConfiguration: selectedMethodWithAttributes.methodDropinConfiguration,
          translations: {
            "en-US": {
              ...translations
            }
          }
        });

        if (paymentContainerRef.current) {
          const dropIn = checkout.create("dropin", {
            showStoredPaymentMethods: false
          });
          dropIn.mount(paymentContainerRef.current);
          setCheckoutInstance(dropIn);
          setDropInMounted(true);
          setDropInLoading(false);
        }
      } catch (err) {
        onError?.(err as Error);
        setDropInMounted(false);
        setDropInLoading(false);
      }
    };
    if (sessionData && selectedMethod) {
      createAdyenCheckout(selectedMethod, sessionData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData]);

  useEffect(() => {
    if (
      !disableLoadingFirstMethodByDefault &&
      !selectedMethod &&
      allowedPaymentMethods.length > 0
    ) {
      handlePaymentMethodClick(allowedPaymentMethods[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedPaymentMethods, disableLoadingFirstMethodByDefault]);

  return (
    <div>
      <div className="mt-4 grid gap-4 grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 items-center">
        {allowedPaymentMethods.map(method => {
          const paymentMethodDisplayAttributes = getPaymentMethodDetails(method);
          return (
            <div
              key={paymentMethodDisplayAttributes.type}
              className={`border rounded p-2 h-16 flex items-center justify-between cursor-pointer hover:shadow-lg ${
                selectedMethod === paymentMethodDisplayAttributes.type
                  ? "border-blue-600"
                  : "border-gray-300"
              }`}
              onClick={() =>
                !dropInLoading && handlePaymentMethodClick(paymentMethodDisplayAttributes.type)
              }
            >
              <div className="flex items-center">
                <img
                  src={paymentMethodDisplayAttributes.logo}
                  alt={paymentMethodDisplayAttributes.name}
                  className="mr-2 h-8 w-auto"
                />
                <div>{paymentMethodDisplayAttributes.name}</div>
              </div>
              {selectedMethod === paymentMethodDisplayAttributes.type && (
                <CheckMarkIcon className="w-4 h-4 text-blue-600 ml-2" />
              )}
            </div>
          );
        })}
      </div>

      {dropInLoading && (
        <div className="flex items-center justify-center my-8">
          <SpinnerIcon className="w-8 h-8 text-gray-400 animate-spin fill-blue-600" />
        </div>
      )}

      <Modal
        isOpen={giactValidationFailureMessageDialogue.open}
        onClose={() => setGiactValidationFailureMessageDialogue({ open: false })}
        hideCancelButton={true}
        onOk={() => setGiactValidationFailureMessageDialogue({ open: false })}
        title={
          <div className="flex items-center space-x-2">
            <ExclamationWarningIcon className="w-5 h-5 text-blue-600" />
            <div className="text-lg font-semibold ">Bank Validation Failed!</div>
          </div>
        }
      >
        <div>
          We have encountered an issue with the bank validation.
          <br />
          {giactValidationFailureMessageDialogue.error &&
            giactValidationFailureMessageDialogue.error}
        </div>
      </Modal>

      {dropInMounted && <div className="font-semibold my-4">Enter Payment Details</div>}
      <div ref={paymentContainerRef} id="dropin-container" className="my-4" />
    </div>
  );
};

export default PaymentForm;
